'use client';

import { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '@/app/store';
import type { ActiveBatchSummary } from '@/app/store/slices/packAnalysisWorkbenchSlice';
import { acknowledgeBatchCompletion } from '@/app/store/slices/packAnalysisWorkbenchSlice';
import { selectLastCompletedBatchSnapshot } from '@/app/store/selectors/packAnalysisWorkbenchSelectors';
import { useTranslation } from '@/app/features/language/hooks/useTranslation';
import { workspaceHref } from '@/app/features/workspace/utils/workspaceRoute';
import { toast } from '@/app/utils/toast';
import { logger } from '@/app/utils/logger';

/**
 * Spec: pack-analysis-ingest-background-progress Reqs 3.x + 4.x + 5.4.
 *
 * Singleton bootstrap hook mounted once from the global Header. Watches the
 * `lastCompletedBatchSnapshot` slice field (populated by the SSE
 * `batch_completed` reducer + the thunk's finally safety-net). When non-null:
 *
 *   - Sonner toast: success when failedFiles === 0; error variant otherwise.
 *     Fires regardless of which page the user is on (Req 3.4).
 *   - Browser Notification: only when `document.visibilityState !== 'visible'`
 *     AND permission has been granted. Wrapped in try/catch so denied /
 *     blocked / unsupported never throws (Req 4.5 + P4).
 *   - Dispatches `acknowledgeBatchCompletion` to clear the snapshot so the
 *     same completion can't fire twice on a re-render.
 *
 * Why this watches a SLICE FIELD instead of using `prevRef` to detect the
 * activeBatch transition: React 18 auto-batches setState across SSE
 * dispatches. The LAST `file_completed` and the `batch_completed` events
 * batch into one render — `prevRef` would then capture the pre-last-file
 * counts (off-by-one toast). Reducers run serially in Redux, so snapshotting
 * INSIDE the batch_completed reducer captures the authoritative final
 * summary (the preceding file_completed for the last file already ran).
 *
 * The hook does NOT subscribe to the SSE stream — that's the thunk's job.
 * It is a pure observer of the snapshot slice field.
 */

export function useIngestBackgroundProgressBoot() {
  const router = useRouter();
  const dispatch = useDispatch();
  const { t } = useTranslation('packAnalysisWorkbench');
  const snapshot = useSelector(selectLastCompletedBatchSnapshot);
  const activeTenant = useSelector((s: RootState) => s.tenant.activeTenant);
  // The Ingest tab lives under the active tenant's workspace
  // (spec: workspace-tenant-code-routing).
  const ingestTabUrl = `${workspaceHref(activeTenant, 'apps/pack-analysis')}?tab=ingest`;

  useEffect(() => {
    if (snapshot == null) return;
    fireCompletionSideEffects(snapshot, t, router, ingestTabUrl);
    dispatch(acknowledgeBatchCompletion());
  }, [snapshot, t, router, dispatch, ingestTabUrl]);
}

function fireCompletionSideEffects(
  summary: ActiveBatchSummary,
  t: (k: string, v?: Record<string, unknown>) => string,
  router: ReturnType<typeof useRouter>,
  ingestTabUrl: string
) {
  const allSucceeded = summary.failedFiles === 0;
  const total = summary.totalFiles;
  const completed = summary.completedFiles;
  const failed = summary.failedFiles;

  const title = allSucceeded
    ? t('ingestProgress.toast.completeSuccessTitle')
    : t('ingestProgress.toast.completeWithFailuresTitle');

  const body = allSucceeded
    ? t('ingestProgress.toast.completeSuccess', { completed, total })
    : t('ingestProgress.toast.completeWithFailures', { failed, total });

  if (allSucceeded) {
    toast.success(body, { title });
  } else {
    toast.error(body, { title });
  }

  // Browser Notification only when the tab isn't visible — in-tab focus is
  // already covered by the Sonner toast. Slack / Linear / Notion convention.
  tryFireBrowserNotification({ title, body, router, ingestTabUrl });
}

interface FireNotificationArgs {
  title: string;
  body: string;
  router: ReturnType<typeof useRouter>;
  ingestTabUrl: string;
}

function tryFireBrowserNotification({ title, body, router, ingestTabUrl }: FireNotificationArgs): void {
  try {
    if (typeof window === 'undefined') return;
    if (!('Notification' in window)) return;
    if (Notification.permission !== 'granted') return;
    if (typeof document === 'undefined') return;
    if (document.visibilityState === 'visible') return;

    const n = new Notification(title, { body });
    n.onclick = () => {
      try {
        window.focus();
        router.push(ingestTabUrl);
      } catch (err) {
        logger.warn('IngestBackgroundProgress: notification.onclick handler threw', err);
      } finally {
        n.close();
      }
    };
  } catch (err) {
    // Permission state machine surprises (Safari quirks, ServiceWorker
    // requirement on some platforms) must never escalate to an unhandled
    // promise rejection. Toast already fired — operators still see it
    // when they return to the tab.
    logger.warn('IngestBackgroundProgress: Browser Notification path failed; falling back to toast-only', err);
  }
}
